import axios from 'axios';
import {
  CardTokenRequest,
  CardTokenResponse,
  ApiResponse,
  PagarmeCard
} from '../types';
import { logError } from '../utils/logger';

// Usa a chave PÚBLICA para tokenização (seguro expor no frontend)
const PUBLIC_KEY = process.env.REACT_APP_PAGARME_PUBLIC_KEY || '';
const API_URL = process.env.REACT_APP_PAGARME_API_URL || 'https://api.pagar.me/core/v5';

// Cliente Axios para tokenização (usa chave pública para segurança)
const tokenClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  auth: {
    username: PUBLIC_KEY,
    password: ''
  }
});

/**
 * Cria um token de cartão de crédito no Pagar.me
 * IMPORTANTE: Os dados do cartão são enviados diretamente para o Pagar.me
 * e NÃO passam pelo nosso backend, garantindo segurança PCI-DSS
 * 
 * @param cardData - Dados do cartão de crédito
 * @returns Token do cartão ou erro
 */
export const createCardToken = async (
  cardData: PagarmeCard
): Promise<ApiResponse<CardTokenResponse>> => {
  const startTime = Date.now();
  const cardBrand = getCardBrand(cardData.number);
  
  try {
    const tokenRequest: CardTokenRequest = {
      type: 'card',
      card: {
        number: cardData.number.replace(/\s/g, ''),
        holder_name: cardData.holder_name,
        exp_month: cardData.exp_month,
        exp_year: cardData.exp_year,
        cvv: cardData.cvv
      }
    };

    const response = await tokenClient.post<CardTokenResponse>('/tokens', tokenRequest);
    const duration = Date.now() - startTime;
    
    return {
      success: true,
      data: response.data
    };
  } catch (error: any) {
    const duration = Date.now() - startTime;
    
    logError('Erro ao tokenizar cartão', {
      error: error.response?.data || error.message,
      status: error.response?.status,
      card_brand: cardBrand,
      duration_ms: duration
    });
    
    return {
      success: false,
      error: error.response?.data || error.message
    };
  }
};

/**
 * Valida o número do cartão de crédito usando o Algoritmo de Luhn
 * @param cardNumber - Número do cartão
 * @returns true se válido, false se inválido
 */
export const validateCardNumber = (cardNumber: string): boolean => {
  const number = cardNumber.replace(/\s/g, '');
  
  if (!/^\d+$/.test(number)) {
    return false;
  }

  let sum = 0;
  let isEven = false;

  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number[i]);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * Identifica a bandeira do cartão baseado no número
 * @param cardNumber - Número do cartão
 * @returns Bandeira do cartão
 */
export const getCardBrand = (cardNumber: string): string => {
  const number = cardNumber.replace(/\s/g, '');
  
  // Visa
  if (/^4/.test(number)) return 'visa';
  
  // Mastercard
  if (/^5[1-5]/.test(number) || /^2[2-7]/.test(number)) return 'mastercard';
  
  // Amex
  if (/^3[47]/.test(number)) return 'amex';
  
  // Elo
  if (/^((((636368)|(438935)|(504175)|(451416)|(636297))\d{0,10})|((5067)|(4576)|(4011))\d{0,12})$/.test(number)) {
    return 'elo';
  }
  
  // Hipercard
  if (/^(606282|3841)/.test(number)) return 'hipercard';
  
  return 'unknown';
};

export default {
  createCardToken,
  validateCardNumber,
  getCardBrand
};
